import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import { FerramanetasDetalhe } from "../../shared/components/ferramentasDaListagem/ferramentas-detalhe";
import { useMutation } from "@tanstack/react-query";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import { usuarioService } from "./service/usuario.service";
import useLoadingStore from "../../shared/store/loading.store";
import { toastError } from "../../shared/services/errors/erros-handler";

import { PessoaFisicaFormPart } from "../../shared/components/formulario/parts/PessoaFisicaFormPart/pessoa-fisica.part";
import InputMascarado from "../../shared/components/formulario/input-mascarado/InputMascarado";
import { mascaraTelefone } from "../../shared/utils/mascaras";
import {
  EditarUsuarioFormData,
  editarUsuarioSchema,
} from "./schema/editar-usuario.schema";
import { InputFileUpload } from "../../shared/components/formulario/input-file/inputFile";
import InputSenha from "../../shared/components/formulario/inputSenha/inputSenha";
import {
  RecuperarSenhaFormData,
  recuperarSenhaSchema,
} from "./schema/recuperar-senha.schema";

export function EditarUsuario() {
  const { id } = useParams<{ id: string }>();
  const { setLoading } = useLoadingStore();
  const [usuario, setUsuario] = useState<any>(null);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState<any>();

  // Formulário principal para editar dados do usuário
  const methods = useForm<EditarUsuarioFormData>({
    resolver: yupResolver(editarUsuarioSchema),
  });

  const passwordMethods = useForm<RecuperarSenhaFormData>({
    resolver: yupResolver(recuperarSenhaSchema),
    defaultValues: {
      password1: "",
      password2: "",
    },
  });

  const { isPending, mutate } = useMutation<any, Error, EditarUsuarioFormData>({
    mutationFn: async (data: EditarUsuarioFormData) => {
      return await usuarioService.editar(id || "", data);
    },
    onMutate: () => {
      setLoading(true, "Atualizando usuário...");
    },
    onSuccess: (r) => {
      toast.success("Usuário atualizado com sucesso");
      // Garante que não vai salvar 'undefined' no localStorage
      /*if (isUpdating?.avatar) {
        localStorage.setItem("avatar", isUpdating.avatar);
        window.dispatchEvent(new Event("storage"));
      }*/
    },
    onError: (err: any) => {
      toastError(err);
    },
    // Garante que o loading será finalizado em caso de sucesso ou erro
    onSettled: () => {
      setLoading(false);
    },
  });

  // Mutação para recuperar a senha
  const { isPending: isPasswordPending, mutate: mutatePassword } = useMutation({
    mutationFn: async (data: RecuperarSenhaFormData) => {
      setLoading(true, "Atualizando senha...");
      await usuarioService.recuperarSenha(id || "", data);
    },
    onSuccess: () => {
      toast.success("Senha atualizada com sucesso");
      setIsPasswordModalOpen(false);
      passwordMethods.reset();
    },
    onError: (err: any) => {
      toastError(err);
    },
  });

  useEffect(() => {
    if (id) {
      usuarioService
        .detalhar(id)
        .then((usuario) => {
          setUsuario(usuario);
          methods.reset(usuario);
        })
        .catch((error) => {
          toastError(error);
        });
    }
  }, [id, methods]);

  const navigate = useNavigate();
  const theme = useTheme();
  const mdDown = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <BaseLayout title="Editar Usuário">
      <FerramanetasDetalhe
        mostraBotaoApagar={false}
        mostraBotaoNovo={false}
        mostraBotaoSalvarEVoltar
        aoClicarBotaoVoltarESalvar={methods.handleSubmit((data) => {
          mutate(data);
          navigate("/usuarios");
        })}
        aoClicarBotaoSalvar={methods.handleSubmit((data) => mutate(data))}
        mostraBotaoVoltar
        aoClicarBotaoVoltar={() => navigate("/usuarios")}
        mostrarBotaoAdicional={true}
        aoClicarBotaoAdicional={() => setIsPasswordModalOpen(true)}
        textoBotaoAdicional="Alterar Senha"
      />
      {/* Formulário Recuperar a senha */}
      <Dialog
        open={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        sx={{ padding: "30px" }}
      >
        <Box
          component="form"
          onSubmit={passwordMethods.handleSubmit((data) =>
            mutatePassword(data)
          )}
        >
          <DialogTitle sx={{ mb: 2 }}>
            Alterar Senha do Usuário: {usuario?.fullName}
          </DialogTitle>
          <DialogContent>
            <InputSenha
              name="password1"
              label="Nova Senha"
              methods={passwordMethods}
              sx={{ mb: 2 }}
            />
            <InputSenha
              name="password2"
              label="Confirme a senha"
              methods={passwordMethods}
            />
          </DialogContent>
          <DialogActions sx={{ padding: "0 24px 20px" }}>
            <Button onClick={() => setIsPasswordModalOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isPasswordPending}>
              Salvar Nova Senha
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Box
        component="form"
        onSubmit={methods.handleSubmit((data) => mutate(data))}
        padding="20px 0"
        display="flex"
        flexDirection="column"
        height="calc(100% - 100px )"
      >
        <PessoaFisicaFormPart methods={methods} mdDown={mdDown} />

        <Box>
          <Typography variant="h5" padding="20px 0">
            Contato:
          </Typography>
          <Box
            display="grid"
            gridTemplateColumns={mdDown ? "1fr" : "1fr 1fr 1fr"}
            gap="20px"
          >
            <TextField
              label="E-mail"
              fullWidth
              {...methods.register("email")}
              error={!!methods.formState.errors.email}
              helperText={methods.formState.errors.email?.message}
              InputLabelProps={{ shrink: true }}
            />

            <InputMascarado
              name="contact1"
              methods={methods}
              label="Telefone"
              mask={mascaraTelefone}
            />

            <InputMascarado
              name="contact2"
              methods={methods}
              label="Telefone"
              mask={mascaraTelefone}
            />
          </Box>
        </Box>

        <br />
        <Controller
          name="active"
          control={methods.control}
          render={({ field }) => (
            <FormControl>
              <FormLabel>Usuário está ativado?</FormLabel>
              <RadioGroup
                row
                value={field.value ? "true" : "false"}
                onChange={(e) => field.onChange(e.target.value === "true")}
              >
                <FormControlLabel
                  value="true"
                  control={<Radio />}
                  label="Sim"
                />
                <FormControlLabel
                  value="false"
                  control={<Radio />}
                  label="Não"
                />
              </RadioGroup>
            </FormControl>
          )}
        />

        <InputFileUpload
          name="avatar"
          label="Adicione um imagem (JPG ou PNG)"
          methods={methods}
        />
      </Box>
    </BaseLayout>
  );
}
